import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../../api/client";
import { usePermission } from "../../../../hooks/usePermission";
import { PERMISSIONS } from "../../../../constants/permissions";
import useAuth from "../../../../hooks/useAuth";
import Toast from "../../components/Toast";
import LogMovementModal from "./LogMovementModal";
import LockableButton from "../../components/LockableButton";
import { verifyModuleEnabled } from "../../utils/moduleAccess";

const API_UPLOAD_URL = process.env.REACT_APP_API_UPLOAD_URL || "";

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #d6dfed",
  borderRadius: 8, fontSize: 14, boxSizing: "border-box",
};

function StockStatusBadge({ status }) {
  const map = {
    out: { bg: "#fee2e2", color: "#dc2626", label: "Out of Stock" },
    low: { bg: "#fff7ed", color: "#c2410c", label: "Low Stock" },
    ok: { bg: "#dcfce7", color: "#15803d", label: "OK" },
  };
  const s = map[status];
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}33`, borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

function StockRangeBar({ current, min, max }) {
  if (min == null && max == null) return null;
  const upper = max != null ? max : Math.max((min || 0) * 2, current, 1);
  const pct = Math.min(100, Math.round((current / upper) * 100));
  const belowMin = min != null && current <= min;
  return (
    <div style={{ marginTop: 4, width: 90 }}>
      <div style={{ height: 6, borderRadius: 999, background: "#f1f5f9", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: belowMin ? "#dc2626" : "#16a34a", borderRadius: 999 }} />
      </div>
    </div>
  );
}

function stockStatus(p) {
  if ((p.currentStock ?? 0) === 0) return "out";
  if (p.minStockLevel != null && p.currentStock <= p.minStockLevel) return "low";
  return "ok";
}

export default function InventoryList() {
  const navigate = useNavigate();
  const canManageInventory = usePermission(PERMISSIONS.MANUFACTURING_INVENTORY_MANAGE);
  const { enabledModules, setEnabledModules } = useAuth();
  const hasInventoryModule = !enabledModules || enabledModules.includes("inventory");
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState(null);

  const handleLogMovementClick = async () => {
    const { allowed, message } = await verifyModuleEnabled("inventory", { enabledModules, setEnabledModules });
    if (!allowed) {
      setToast({ msg: message, type: "error" });
      return;
    }
    setModal(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get("/manufacturing/parts");
      setParts(resp.data.data || []);
    } catch {
      setToast({ msg: "Failed to load parts", type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = parts.filter((p) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
    const matchesStatus = !statusFilter || stockStatus(p) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "0 0 20px", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Inventory</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Live stock levels for every part.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/manufacturing/inventory/movements" className="btn-secondary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            View Movement History
          </Link>
          {canManageInventory && (
            <LockableButton
              className="btn-primary"
              onClick={handleLogMovementClick}
              locked={!hasInventoryModule}
              lockedTitle="Inventory module is not enabled for your company"
            >
              + Log Movement
            </LockableButton>
          )}
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <input
            style={{ ...inputStyle, width: 260 }}
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select style={{ ...inputStyle, width: 160 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="ok">OK</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            {parts.length === 0 ? "No parts yet. Add parts in Settings → Parts Master." : "No parts match your filters."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Part", "Code", "Category", "Current Stock", "Min / Max", "Unit Cost", "Location", "Supplier", "Status"].map((h) => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, idx) => {
                  const status = stockStatus(p);
                  return (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/manufacturing/inventory/movements?partMasterId=${p.id}`)}
                      style={{ borderBottom: idx < filtered.length - 1 ? "1px solid #f1f5f9" : "none", background: status === "out" ? "#fff5f5" : "transparent", cursor: "pointer" }}
                      title="View movement history for this part"
                    >
                      <td style={{ padding: "10px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {p.imageFileName ? (
                            <img src={`${API_UPLOAD_URL}${p.imageFileName}`} alt={p.name} style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 6, border: "1px solid #e4ebf5", flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 32, height: 32, borderRadius: 6, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>⚙️</div>
                          )}
                          <span style={{ fontWeight: 600, color: "#0f2d5c" }}>{p.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: "10px 16px", color: "#64748b", fontFamily: "monospace" }}>{p.code}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.category?.name || "—"}</td>
                      <td style={{ padding: "10px 16px" }}>
                        <span style={{ fontWeight: 700, color: status === "ok" ? "#0f2d5c" : "#dc2626" }}>
                          {p.currentStock ?? 0} {p.unitOfMeasure}
                        </span>
                        <StockRangeBar current={p.currentStock ?? 0} min={p.minStockLevel} max={p.maxStockLevel} />
                      </td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>
                        {p.minStockLevel ?? "—"} / {p.maxStockLevel ?? "—"}
                      </td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.unitCost != null ? `₹${p.unitCost}` : "—"}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.location?.name || "—"}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{p.supplier?.name || "—"}</td>
                      <td style={{ padding: "10px 16px" }}><StockStatusBadge status={status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <LogMovementModal
          parts={parts}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); setToast({ msg: "Movement logged", type: "success" }); }}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
